import {defineStore} from 'pinia'
import {
  useDirectConversationApiStore,
} from '@toolify/client/src/stores/api/DirectConversationApiStore/useDirectConversationApiStore'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {
  IDirectConversationResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDirectConversationResponseInstance'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {useSocketStore} from '@toolify/client/src/stores/SocketStore/useSocketStore'
import {
  INotificationResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/INotificationResponseInstance'
import {NotificationType} from '@toolify/server/src/models/mongoose/NotificationModel/enum/NotificationType'
import {mapNotificationResponseInstance} from '@toolify/client/src/modules/api/singletons/mapNotificationResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {ConversationType} from '@toolify/server/src/models/mongoose/MessageModel/enum/ConversationType'
import {
  INewMessageNotificationTypeInterfaces,
} from '@toolify/server/src/services/NotificationService/strategies/INewMessageNotificationTypeInterfaces'
import {computed, ref, watch} from 'vue'
import {getEntityNameByEntity} from '@toolify/client/src/modules/entities/functions/getEntityNameByEntity'
import {useActivityItemSocketListener} from '@toolify/client/src/composables/useActivityItemSocketListener'
import {useSocketListener} from '@toolify/client/src/composables/useSocketListener'
import {
  IResourceReadAtChangeEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceReadAtChangeEmitPayload'
import {
  IResourceArchivedAtChangeEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceArchivedAtChangeEmitPayload'
import {
  IDirectConversationNameChangedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IDirectConversationNameChangedEmitPayload'
import {ActivityItemType} from '@toolify/server/src/models/mongoose/ActivityItemModel/enum/ActivityItemType'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {i18n} from '@toolify/client/src/setup/i18n'
import {EntityType} from '@toolify/server/src/services/EntityService/enum/EntityType'
import {IResponseEntity} from '@toolify/server/src/services/EntityService/types/IResponseEntity'
import {isAgentDirectConversation} from '@toolify/client/src/modules/chat/functions/isAgentDirectConversation'

export const useDirectConversationStore = defineStore('directConversation', () => {
  const directConversationApiStore = useDirectConversationApiStore()
  const authStore = useAuthStore()
  const socketStore = useSocketStore()

  const directConversations = ref<IDirectConversationResponseInstance[] | null>(null)
  const refreshDirectConversationPromises = new Map<string, Promise<IDirectConversationResponseInstance | null>>()

  async function initialize() {
    socketStore.socket.on(GlobalSocketEventType.NotificationCreated, onNotificationCreated)
    messageCreatedListener.start()
    messageUpdatedListener.start()
    messageDeletedListener.start()
    resourceReadAtChangeListener.start()
    resourceArchivedAtChangeListener.start()
    directConversationNameChangedListener.start()
  }
  function dispose() {
    socketStore.socket.off(GlobalSocketEventType.NotificationCreated, onNotificationCreated)
    messageCreatedListener.stop()
    messageUpdatedListener.stop()
    messageDeletedListener.stop()
    resourceReadAtChangeListener.stop()
    resourceArchivedAtChangeListener.stop()
    directConversationNameChangedListener.stop()
  }

  function upsertDirectConversation(
    refreshedDirectConversation: IDirectConversationResponseInstance,
  ): IDirectConversationResponseInstance {
    const existingDirectConversations = directConversations.value || []
    directConversations.value = [
      ...existingDirectConversations.filter(directConversation => {
        return directConversation.id !== refreshedDirectConversation.id
      }),
      refreshedDirectConversation,
    ]
    return refreshedDirectConversation
  }

  async function refreshDirectConversation(directConversationId: string): Promise<IDirectConversationResponseInstance | null> {
    if(!directConversationId) {
      return null
    }
    const inFlightRefresh = refreshDirectConversationPromises.get(directConversationId)
    if(inFlightRefresh) {
      return inFlightRefresh
    }
    const refreshPromise = (async () => {
      try {
        const getDirectConversationResponse = await directConversationApiStore.getDirectConversation(directConversationId)
        return upsertDirectConversation(getDirectConversationResponse.directConversation)
      } catch {
        return null
      } finally {
        refreshDirectConversationPromises.delete(directConversationId)
      }
    })()
    refreshDirectConversationPromises.set(directConversationId, refreshPromise)
    return refreshPromise
  }

  async function onDirectConversationMessageActivity({
    resourcePathItemMap,
  }: {
    resourcePathItemMap: Map<ResourceType, {resourceId: string}>
  }) {
    const directConversationResource = resourcePathItemMap.get(ResourceType.DirectConversation)
    if(!directConversationResource) {
      return
    }
    await refreshDirectConversation(directConversationResource.resourceId)
  }

  const messageCreatedListener = useActivityItemSocketListener(
    ActivityItemType.MessageCreated,
    onDirectConversationMessageActivity,
    null,
    {
      useLifecycleHooks: false,
    },
  )

  const messageUpdatedListener = useActivityItemSocketListener(
    ActivityItemType.MessageUpdated,
    onDirectConversationMessageActivity,
    null,
    {
      useLifecycleHooks: false,
    },
  )

  const messageDeletedListener = useActivityItemSocketListener(
    ActivityItemType.MessageDeleted,
    onDirectConversationMessageActivity,
    null,
    {
      useLifecycleHooks: false,
    },
  )

  function onResourceReadAtChange(payload: IResourceReadAtChangeEmitPayload) {
    if(payload.resourceType !== ResourceType.DirectConversation) {
      return
    }
    if(payload.readAt) {
      markLocalDirectConversationAsRead(payload.resourceId, new Date(payload.readAt))
    } else {
      markLocalDirectConversationAsUnread(payload.resourceId)
    }
  }

  const resourceReadAtChangeListener = useSocketListener<IResourceReadAtChangeEmitPayload>(
    GlobalSocketEventType.ResourceReadAtChange,
    onResourceReadAtChange,
    {
      useLifecycleHooks: false,
    },
  )

  function onResourceArchivedAtChange(payload: IResourceArchivedAtChangeEmitPayload) {
    if(payload.resourceType !== ResourceType.DirectConversation) {
      return
    }
    patchLocalDirectConversation(payload.resourceId, {archivedAt: payload.archivedAt ? new Date(payload.archivedAt) : null})
  }

  const resourceArchivedAtChangeListener = useSocketListener<IResourceArchivedAtChangeEmitPayload>(
    GlobalSocketEventType.ResourceArchivedAtChange,
    onResourceArchivedAtChange,
    {
      useLifecycleHooks: false,
    },
  )

  function onDirectConversationNameChanged(payload: IDirectConversationNameChangedEmitPayload) {
    patchLocalDirectConversation(payload.directConversationId, {name: payload.name})
  }

  const directConversationNameChangedListener = useSocketListener<IDirectConversationNameChangedEmitPayload>(
    GlobalSocketEventType.DirectConversationNameChanged,
    onDirectConversationNameChanged,
    {
      useLifecycleHooks: false,
    },
  )

  async function fetchDirectConversations() {
    const getDirectConversationsResponse = await directConversationApiStore.getDirectConversations()
    directConversations.value = getDirectConversationsResponse.directConversations
  }

  watch(() => {
    return authStore.currentEntity?.entityType === EntityType.User ? authStore.currentEntity.entityId : null
  }, async (currentUserId) => {
    if(currentUserId) {
      await fetchDirectConversations()
    } else {
      directConversations.value = null
    }
  })

  let hasSocketConnected = socketStore.isConnected
  watch(() => socketStore.isConnected, async (isConnected) => {
    if(!isConnected) {
      return
    }
    if(!hasSocketConnected) {
      hasSocketConnected = true
      return
    }
    if(authStore.currentEntity?.entityType === EntityType.User) {
      await fetchDirectConversations()
    }
  })

  async function onNotificationCreated({notification}: {
    notification: INotificationResponseInstance<NotificationType>
  }) {
    const mappedNotification = mapNotificationResponseInstance(notification as unknown as IToJson<INotificationResponseInstance<NotificationType>>)
    if(mappedNotification.type === NotificationType.NewMessage) {
      const newMessageNotification = mappedNotification as INotificationResponseInstance<NotificationType.NewMessage>
      if(newMessageNotification.payload.conversationType === ConversationType.Direct) {
        const newMessagePayload = newMessageNotification.payload as INewMessageNotificationTypeInterfaces<ConversationType.Direct>['payload']
        const directConversationId = newMessagePayload.conversationPayload.directConversationId
        const foundDirectConversation = directConversations.value?.find(directConversation => {
          return directConversation.id === directConversationId
        })

        if(foundDirectConversation) {
          directConversations.value = directConversations.value.map(directConversation => {
            if(directConversation.id === directConversationId) {
              return {
                ...directConversation,
                lastActivityAt: new Date(),
                unreadNotificationCount: directConversation.unreadNotificationCount + 1,
                latestMessageText: newMessagePayload.truncatedContent,
              }
            }
            return directConversation
          })
        } else {
          await refreshDirectConversation(directConversationId)
        }
      }
    }
  }

  function patchLocalDirectConversation(directConversationId: string, patch: Partial<IDirectConversationResponseInstance>) {
    if(directConversations.value) {
      directConversations.value = directConversations.value.map(directConversation => {
        return directConversation.id === directConversationId ? {
          ...directConversation,
          ...patch,
        } : directConversation
      })
    }
  }

  function markLocalDirectConversationAsRead(directConversationId: string, readAt: Date = new Date()) {
    patchLocalDirectConversation(directConversationId, {
      unreadNotificationCount: 0,
      readAt,
    })
  }

  function markLocalDirectConversationAsUnread(directConversationId: string) {
    patchLocalDirectConversation(directConversationId, {readAt: null})
  }

  async function patchLocalDirectConversationUntilSaved(
    directConversationId: string,
    patch: Partial<IDirectConversationResponseInstance>,
    save: () => Promise<Partial<IDirectConversationResponseInstance>>,
  ): Promise<void> {
    const previousDirectConversation = directConversations.value?.find(directConversation => directConversation.id === directConversationId)
    const rollbackPatch = Object.fromEntries(Object.keys(patch).map(key => [key, previousDirectConversation?.[key] ?? null]))
    patchLocalDirectConversation(directConversationId, patch)
    try {
      patchLocalDirectConversation(directConversationId, await save())
    } catch(error) {
      patchLocalDirectConversation(directConversationId, rollbackPatch)
      throw error
    }
  }

  async function renameDirectConversation(directConversationId: string, name: string): Promise<void> {
    await patchLocalDirectConversationUntilSaved(directConversationId, {name}, async () => {
      await directConversationApiStore.updateDirectConversationName(directConversationId, name)
      return {}
    })
  }

  async function setDirectConversationArchived(directConversationId: string, isArchived: boolean): Promise<void> {
    await patchLocalDirectConversationUntilSaved(directConversationId, {archivedAt: isArchived ? new Date() : null}, async () => {
      const {archivedAt} = await directConversationApiStore.setDirectConversationArchived(directConversationId, isArchived)
      return {archivedAt: archivedAt ? new Date(archivedAt) : null}
    })
  }

  const systemEntity = {
    entityType: EntityType.System,
    entityId: null,
    payload: null,
  } as IResponseEntity

  const mappedDirectConversations = computed(() => {
    if(!directConversations.value) {
      return null
    }
    return directConversations.value.sort((a, b) => {
      if(!a.lastActivityAt) {
        return 1
      }
      if(!b.lastActivityAt) {
        return -1
      }
      return b.lastActivityAt.getTime() - a.lastActivityAt.getTime()
    }).map(directConversation => {
      const maxAvatarCount = directConversation.totalMemberCount > 4 ? 3 : directConversation.totalMemberCount

      const cachedFirstEntitiesExceptMe = directConversation.cachedFirstEntities.filter(entity => {
        if(!directConversation.isGroup) {
          return entity.entityId !== authStore.currentEntity?.entityId || entity.entityType !== authStore.currentEntity?.entityType
        }
        return true
      })
      let isUnread = false
      if(directConversation.lastActivityAt) {
        isUnread = true
      }
      if(directConversation.lastActivityAt && directConversation.readAt) {
        isUnread = directConversation.lastActivityAt > directConversation.readAt
      }
      const avatarEntities = isAgentDirectConversation(directConversation)
        ? [systemEntity]
        : cachedFirstEntitiesExceptMe.slice(0, maxAvatarCount)
      return {
        name: directConversation.name || (avatarEntities.length ? avatarEntities.map(entity => {
          return getEntityNameByEntity(entity)
        }).join(', ') : i18n.global.t('chat.untitledConversation')),
        customName: directConversation.name || null,
        avatarEntities,
        cachedFirstEntities: directConversation.cachedFirstEntities ?? [],
        isAiConversation: directConversation.isAiConversation,
        thumbnailCount: Math.min(4, avatarEntities.length),
        totalMemberCount: directConversation.totalMemberCount,
        id: directConversation.id,
        latestMessageText: directConversation.latestMessageText || i18n.global.t('chat.emptyConversation'),
        to: {
          name: RouteName.DirectConversation,
          params: {
            directConversationId: directConversation.id,
          },
        },
        isUnread,
        unreadCount: directConversation.unreadNotificationCount,
        lastActivityAt: directConversation.lastActivityAt,
        hasMessages: !!directConversation.latestMessage,
        isArchived: !!directConversation.archivedAt,
      }
    })
  })

  function isDirectConversationUnread(directConversationId: string): boolean {
    return mappedDirectConversations.value?.find(directConversation => {
      return directConversation.id === directConversationId
    })?.isUnread ?? false
  }

  return {
    initialize,
    dispose,
    mappedDirectConversations,
    isDirectConversationUnread,
    markLocalDirectConversationAsRead,
    markLocalDirectConversationAsUnread,
    setDirectConversationArchived,
    renameDirectConversation,
    refreshDirectConversation,
    upsertDirectConversation,
  }
})
